// "What we currently know" — the client page's last zone, and its standing facts.
//
// Layer 2 of the timeline spec, rendered as a BRIEFING FORM over the standing fact catalog
// (the server ships the catalog with the facts — one source of truth). Two things changed
// when the client page became the standing brief, and neither touched the logic:
//   · grouped by CONVERSATION (Money · Timing · Solution · People · Relationship), not by
//     namespace — the mapping is the server's, shipped as `group` on each catalog prefix;
//   · every value carries its claim mark (▪ held / ◇ proposed), and every quote is the
//     record's voice closed by its source ref.
// The catalog's empty standing fields are still here — the briefing form keeps its blanks —
// behind one toggle, because a zone of thirty dashes is not a brief.
// Create, edit, confirm, retract, delete and the supersession chain are unchanged.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Pencil, Sparkles, Trash2 } from 'lucide-react';

import { useToast } from './Toast.js';
import ConfirmModal from './ConfirmModal.js';
import { documentsApi, type LibraryDocumentRow } from '../api/documents.js';
import { timelineApi, type FactCatalogPrefix, type FactGroup, type FactRow } from '../api/timeline.js';
import { useLocale } from '../i18n/index.js';
import './ClientFactsPanel.de.js';
import './ClientFactsPanel.fr.js';

/** `t()` for a sentence with markup inside: each `{name}` placeholder is filled with a React node. */
function tx(t: (key: string) => string, key: string, nodes: Record<string, ReactNode>): ReactNode {
  return t(key).split(/(\{\w+\})/g).map((part, i) => {
    const m = /^\{(\w+)\}$/.exec(part);
    return m ? <Fragment key={i}>{nodes[m[1]]}</Fragment> : part;
  });
}

/** The five conversational groups, in the order the client page states them (server: FACT_GROUPS). */
const GROUP_ORDER = ['Money', 'Timing', 'Solution', 'People', 'Relationship'] as const;

interface Props {
  clientId: number;
  /** Bump to reload from outside — e.g. after a reconstruction pass lands facts. */
  refreshToken?: number;
}

interface FactDraft {
  key: string;
  value: string;
  as_of: string;
  stated_by: string;
  source_document_id: string;
  quote: string;
  /** Standing fields have priority — an off-catalog key needs this consciously ticked. */
  novel: boolean;
}

interface FactEditDraft {
  id: number;
  value: string;
  as_of: string;
  stated_by: string;
  quote: string;
}

export default function ClientFactsPanel({ clientId, refreshToken = 0 }: Props) {
  const toast = useToast();
  const { t, tp, fmtDate } = useLocale();

  const [facts, setFacts] = useState<FactGroup[] | null>(null);
  const [catalog, setCatalog] = useState<FactCatalogPrefix[] | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  /** Agent rows the operator confirmed this session (created_by_kind stays 'agent' in the DB). */
  const [confirmedFacts, setConfirmedFacts] = useState<Set<number>>(new Set());

  /** The zone's two filters: the unconfirmed cut, and whether the catalog's empty fields show. */
  const [onlyUnconfirmed, setOnlyUnconfirmed] = useState(false);
  const [showEmpty, setShowEmpty] = useState(false);

  const [factDraft, setFactDraft] = useState<FactDraft | null>(null);
  const [factSaving, setFactSaving] = useState(false);
  const [factEdit, setFactEdit] = useState<FactEditDraft | null>(null);

  const [confirm, setConfirm] = useState<
    { id: number; label: string; isCurrent: boolean; hasPredecessor: boolean } | null
  >(null);
  const [busy, setBusy] = useState(false);

  /** Whole-library rows for the fact source select — loaded lazily on first "Add fact". */
  const [libraryDocs, setLibraryDocs] = useState<LibraryDocumentRow[] | null>(null);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const loadFacts = useCallback(async (silent = false) => {
    try {
      const res = await timelineApi.facts(clientId, { history: true });
      if (mounted.current) {
        setFacts(res.facts);
        setCatalog(res.catalog ?? null);
      }
    } catch (err) {
      if (!silent) toast('err', (err as Error).message);
    }
  }, [clientId, toast]);

  useEffect(() => { void loadFacts(refreshToken > 0); }, [loadFacts, refreshToken]);

  const ensureLibrary = useCallback(async () => {
    if (libraryDocs) return libraryDocs;
    try {
      const res = await documentsApi.library(clientId);
      if (mounted.current) setLibraryDocs(res.rows);
      return res.rows;
    } catch (err) {
      toast('err', (err as Error).message);
      return [];
    }
  }, [clientId, libraryDocs, toast]);

  // ── fact mutations ───────────────────────────────────────────────────────────

  async function openFactCreate() {
    void ensureLibrary();
    setFactDraft({ key: '', value: '', as_of: '', stated_by: '', source_document_id: '', quote: '', novel: false });
  }

  async function saveNewFact() {
    if (!factDraft) return;
    setFactSaving(true);
    try {
      const created = await timelineApi.createFact({
        client_id: clientId,
        key: factDraft.key.trim(),
        value: factDraft.value.trim(),
        as_of: factDraft.as_of || null,
        stated_by: factDraft.stated_by.trim() || null,
        source_document_id: Number(factDraft.source_document_id) || undefined,
        quote: factDraft.quote.trim() || null,
        ...(factDraft.novel ? { novel: true } : {}),
      });
      toast('ok', created.superseded_fact_id
        ? t('Recorded — the previous value of {key} moved into its history', { key: created.key })
        : t('Fact recorded'));
      setFactDraft(null);
      await loadFacts(true);
    } catch (err) {
      toast('err', (err as Error).message); // 422 carries the corrective quote message verbatim
    } finally {
      setFactSaving(false);
    }
  }

  async function saveFactEdit() {
    if (!factEdit) return;
    setFactSaving(true);
    try {
      await timelineApi.updateFact(factEdit.id, {
        value: factEdit.value.trim(),
        as_of: factEdit.as_of || null,
        stated_by: factEdit.stated_by.trim() || null,
        quote: factEdit.quote.trim() || null,
      });
      toast('ok', t('Fact updated'));
      setFactEdit(null);
      await loadFacts(true);
    } catch (err) {
      toast('err', (err as Error).message);
    } finally {
      setFactSaving(false);
    }
  }

  /** One-click keep for an agent row — an audited operator touch; the row stays agent-attributed. */
  async function confirmFact(row: FactRow) {
    try {
      await timelineApi.updateFact(row.id, { confirm: true });
      setConfirmedFacts((prev) => new Set(prev).add(row.id));
      toast('ok', t('Confirmed — {key} is operator-reviewed (audit log has the touch).', { key: row.key }));
    } catch (err) {
      toast('err', (err as Error).message);
    }
  }

  async function retractFact(row: FactRow) {
    try {
      await timelineApi.updateFact(row.id, { status: 'retracted' });
      toast('ok', t('{key} retracted — the row stays in the history.', { key: row.key }));
      await loadFacts(true);
    } catch (err) {
      toast('err', (err as Error).message);
    }
  }

  async function runDelete() {
    if (!confirm) return;
    setBusy(true);
    try {
      const out = await timelineApi.deleteFact(confirm.id);
      toast('ok', out.restored_fact_id
        ? t('Deleted — the previous value of {key} is current again', { key: out.key })
        : t('Deleted {key}', { key: out.key }));
      await loadFacts(true);
      setConfirm(null);
    } catch (err) {
      toast('err', (err as Error).message);
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  }

  // ── rendering ────────────────────────────────────────────────────────────────

  /** One rendered line of the briefing: a standing slot (filled or empty) or a family instance. */
  interface BriefingRow {
    label: string;
    rawKey: string;
    group: FactGroup | null; // null = notable empty exact slot, rendered as a quiet "—"
  }

  /**
   * The catalog drives the layout: fixed prefix order, exact slots always listed (filled or
   * "—"), family instances only as recorded (empty families are not enumerable — skipped).
   * Whatever no slot claims — uncatalogued novel keys, legacy strays — goes to the quiet
   * "Client-specific" section, raw key shown.
   */
  const briefing = useMemo(() => {
    const groups = facts ?? [];
    const claimed = new Set<string>();
    const prefixes = (catalog ?? []).map((ns) => {
      const group = ns.group;
      const rows: BriefingRow[] = [];
      for (const slot of ns.slots) {
        if (slot.key) {
          const rawKey = `${ns.prefix}.${slot.key}`;
          const group = groups.find((g) => g.key === rawKey) ?? null;
          if (group) claimed.add(group.key);
          rows.push({ label: slot.label, rawKey, group });
        } else if (slot.family) {
          const stem = `${ns.prefix}.${slot.family}.`;
          const instances = groups
            .filter((g) => g.key.startsWith(stem) && g.key.length > stem.length && !g.current?.uncatalogued)
            .sort((a, b) => (a.key < b.key ? -1 : 1));
          for (const group of instances) {
            claimed.add(group.key);
            rows.push({ label: `${slot.label} · ${group.key.slice(stem.length)}`, rawKey: group.key, group });
          }
        }
      }
      return { prefix: ns.prefix, group, rows };
    });
    const clientSpecific = groups
      .filter((g) => !claimed.has(g.key))
      .sort((a, b) => (a.key < b.key ? -1 : 1));
    // The page groups by CONVERSATION, not by namespace: five fixed groups, in this order,
    // each carrying the prefixes the server maps to it. Empty groups are omitted.
    const conversational = GROUP_ORDER.map((group) => {
      const members = prefixes.filter((p) => p.group === group);
      return {
        group,
        prefixes: members.map((p) => p.prefix),
        rows: members.flatMap((p) => p.rows),
      };
    }).filter((g) => g.rows.length > 0);
    return { conversational, clientSpecific };
  }, [facts, catalog]);

  const unconfirmedCount = useMemo(
    () => (facts ?? []).filter((g) => g.current && g.current.created_by_kind === 'agent' && !g.current.confirmed_at
      && !confirmedFacts.has(g.current.id)).length,
    [facts, confirmedFacts],
  );
  const factCount = (facts ?? []).filter((g) => g.current).length;

  /** History in chain order, newest first: walk supersedes_fact_id from the current node. */
  function chainHistory(group: FactGroup): FactRow[] {
    const rows = group.history ?? [];
    const byId = new Map(rows.map((r) => [r.id, r]));
    const ordered: FactRow[] = [];
    let cursor = group.current?.supersedes_fact_id ?? null;
    const seen = new Set<number>();
    while (cursor && byId.has(cursor) && !seen.has(cursor)) {
      const row = byId.get(cursor)!;
      ordered.push(row);
      seen.add(cursor);
      cursor = row.supersedes_fact_id;
    }
    for (const r of rows) if (!seen.has(r.id)) ordered.push(r); // retracted / dangling nodes
    return ordered;
  }

  function sourceLink(row: FactRow) {
    return (
      <a
        className="mono text-xs"
        style={{ color: 'var(--ink-3)', textDecoration: 'underline', textUnderlineOffset: 2 }}
        href={documentsApi.downloadUrl(row.source_document_id)}
        target="_blank"
        rel="noreferrer"
        title={row.source_document_filename}
      >
        {row.source_document_ref}
      </a>
    );
  }

  function factMeta(row: FactRow) {
    return (
      <div className="flex items-center gap-8 flex-wrap text-xs text-ink-3">
        {row.as_of && <span className="mono">{t('as of {date}', { date: fmtDate(row.as_of) })}</span>}
        {row.stated_by && <span>· {row.stated_by}</span>}
        <span>· {t('source')} {sourceLink(row)}</span>
        {row.created_by_kind === 'agent' && (
          <span className="badge badge-plain" title={t('Recorded by the reconstruction agent — always yours to correct')}>
            <Sparkles className="icon-sm" style={{ width: 10, height: 10 }} /> {t('agent')}
          </span>
        )}
        {row.status === 'retracted' && <span className="badge badge-plain">{t('retracted')}</span>}
      </div>
    );
  }

  const toggleKey = (key: string) => setExpandedKeys((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  /**
   * One fact: label · value · as-of, opening onto its evidence and its verbs.
   *
   * The claim mark (ATTEST LAW 6) is the whole reading at a glance — ▪ a value a human holds,
   * ◇ a value an agent proposed and nobody has confirmed. The quote is the RECORD's voice
   * (serif) and never appears without the ref that closes it.
   */
  function renderFactRow(group: FactGroup, label: string, monoLabel = false) {
    const row = group.current;
    const expanded = expandedKeys.has(group.key);
    const editing = factEdit && row && factEdit.id === row.id;

    if (!row) {
      return (
        <div key={group.key} className="client-fact">
          <div className="client-fact-top">
            <span className={`client-fk${monoLabel ? ' src-ref' : ''}`} title={group.key}>{label}</span>
            <span className="client-fval n-none voice-figure">
              {tp(group.history_count, 'no current value — {n} historical row', 'no current value — {n} historical rows')}
            </span>
          </div>
        </div>
      );
    }

    const unheld = row.created_by_kind === 'agent' && !row.confirmed_at && !confirmedFacts.has(row.id);
    if (onlyUnconfirmed && !unheld) return null;
    const history = expanded ? chainHistory(group) : [];

    return (
      <div key={group.key} className="client-fact">
        <button type="button" className="client-fact-top" onClick={() => toggleKey(group.key)}>
          <span className={`client-fk${monoLabel ? ' src-ref' : ''}`} title={group.key}>{label}</span>
          <span className="client-fval">
            <span
              className={`att ${unheld ? 'att--proposed' : 'att--held'}`}
              title={unheld ? t('Recorded by an agent pass, never confirmed by a human') : t('Held — a human stands behind this value')}
            />{' '}
            {row.value}
            {unheld && <span className="tag-unheld"> {t('unconfirmed')}</span>}
          </span>
          <span className="client-fmeta voice-figure">
            {row.as_of ? fmtDate(row.as_of) : <span className="n-unknown">{t('no date')}</span>}
          </span>
        </button>

        {expanded && (
          <div className="client-fdet">
            {row.quote ? (
              <>
                <div className="ev">“{row.quote}”</div>
                <div className="ev-src">
                  <span className="src-ref">{row.source_document_ref}</span>
                  <span className="sep">·</span>
                  <span>{row.stated_by ? t('stated by {name}', { name: row.stated_by }) : t('no speaker attributed')}</span>
                </div>
              </>
            ) : (
              <div className="ev-src" style={{ marginTop: 0 }}>
                <span className="src-ref">{row.source_document_ref}</span>
                <span className="sep">·</span>
                <span>{row.stated_by ? t('stated by {name}', { name: row.stated_by }) : t('extracted from the document — no speaker attributed')}</span>
              </div>
            )}

            <div className="client-fdet-src">
              <span className="src-ref">{group.key}</span>
              {row.as_of && <span className="voice-figure">{t('as of {date}', { date: fmtDate(row.as_of) })}</span>}
              {row.created_by_kind === 'agent' && (
                <span className="badge badge-plain" title={t('Recorded by the reconstruction agent — always yours to correct')}>
                  <Sparkles className="icon-sm" style={{ width: 10, height: 10 }} /> {t('agent')}
                </span>
              )}
              {sourceLink(row)}
              {group.history_count > 0 && (
                <span className="voice-figure">
                  {tp(group.history_count, '{n} earlier value', '{n} earlier values')}
                </span>
              )}
            </div>

            {!editing && (
              <div className="client-fdet-act">
                {unheld && (
                  <button className="btn btn-sm" title={t('Keep this agent-recorded value — one audited click')} onClick={() => void confirmFact(row)}>
                    {t('Confirm')}
                  </button>
                )}
                <button
                  className="btn btn-sm btn-ghost"
                  title={t('Edit value, as-of, stated-by or quote')}
                  onClick={() => setFactEdit({
                    id: row.id,
                    value: row.value,
                    as_of: row.as_of ? row.as_of.slice(0, 10) : '',
                    stated_by: row.stated_by ?? '',
                    quote: row.quote ?? '',
                  })}
                >
                  <Pencil className="icon-sm" style={{ width: 12, height: 12 }} /> {t('Edit')}
                </button>
                <button className="btn btn-sm btn-ghost" title={t('Retract — wrong or withdrawn; the row stays in the history')} onClick={() => void retractFact(row)}>
                  {t('Retract')}
                </button>
                <button
                  className="btn btn-sm btn-ghost"
                  title={row.supersedes_fact_id ? t('Delete — the previous value becomes current again') : t('Delete this fact')}
                  onClick={() => setConfirm({
                    id: row.id, label: group.key,
                    isCurrent: true, hasPredecessor: !!row.supersedes_fact_id,
                  })}
                >
                  <Trash2 className="icon-sm" style={{ width: 12, height: 12 }} /> {t('Delete')}
                </button>
              </div>
            )}

            {editing && factEdit && (
              <div className="mt-8" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s8)' }}>
                <input className="form-input" value={factEdit.value} onChange={(e) => setFactEdit({ ...factEdit, value: e.target.value })} />
                <div className="flex gap-8">
                  <input className="form-input" type="date" style={{ width: 150 }} value={factEdit.as_of}
                    onChange={(e) => setFactEdit({ ...factEdit, as_of: e.target.value })} />
                  <input className="form-input flex-1" placeholder={t('Stated by')} value={factEdit.stated_by}
                    onChange={(e) => setFactEdit({ ...factEdit, stated_by: e.target.value })} />
                </div>
                <textarea
                  className="form-textarea" rows={2}
                  placeholder={t('Quote — verbatim from the source document, or empty')}
                  value={factEdit.quote}
                  onChange={(e) => setFactEdit({ ...factEdit, quote: e.target.value })}
                />
                <div className="flex gap-8">
                  <button className="btn btn-sm btn-accent" disabled={factSaving} onClick={() => void saveFactEdit()}>
                    {factSaving ? t('Saving…') : t('Save')}
                  </button>
                  <button className="btn btn-sm btn-ghost" onClick={() => setFactEdit(null)}>{t('Cancel')}</button>
                </div>
              </div>
            )}

            {history.length > 0 && (
              <div className="fact-history">
                {history.map((h) => (
                  <div key={h.id}>
                    <div className="text-sm text-ink-2" style={{ lineHeight: 1.5, overflowWrap: 'anywhere' }}>{h.value}</div>
                    <div className="flex items-center gap-8 mt-4">
                      {factMeta(h)}
                      <button
                        className="btn btn-sm btn-ghost ml-auto"
                        title={t('Delete this historical row — the chain re-links around it')}
                        onClick={() => setConfirm({ id: h.id, label: h.key, isCurrent: false, hasPredecessor: !!h.supersedes_fact_id })}
                      >
                        <Trash2 className="icon-sm" style={{ width: 12, height: 12 }} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <section className="zone" id="facts">
      <div className="zone-head">
        <span className="u-label">{t('What we currently know')}</span>
        <span className="zone-count voice-figure">
          {tp(factCount, '{n} standing fact', '{n} standing facts')} · {t('{n} unconfirmed', { n: unconfirmedCount })}
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--s8)', flexWrap: 'wrap' }}>
          <button
            className={`btn btn-sm${onlyUnconfirmed ? ' btn-accent' : ' btn-ghost'}`}
            title={t('Show only the values an agent recorded and nobody has confirmed')}
            onClick={() => setOnlyUnconfirmed((v) => !v)}
          >
            {t('Only unconfirmed ({n})', { n: unconfirmedCount })}
          </button>
          <button
            className="btn btn-sm btn-ghost"
            title={t('The catalog’s standing fields that hold no value yet — the briefing form, with its blanks')}
            onClick={() => setShowEmpty((v) => !v)}
          >
            {showEmpty ? t('Hide the empty fields') : t('Show the empty fields')}
          </button>
          <button className="btn btn-sm" onClick={() => void openFactCreate()}>{t('Add fact')}</button>
        </span>
      </div>

      {facts && !facts.length && (
        <p className="zone-empty">
          {t('Nothing recorded yet — run a reconstruct pass. Facts are the latest known instance of each negotiable — fee, scope bound, lead time — each with its as-of date and source document.')}
        </p>
      )}

      {facts && facts.length > 0 && (
        <div className="sheet sheet--pad client-facts">
          {briefing.conversational.map(({ group, prefixes, rows }) => {
            const rendered = rows.map((r) =>
              r.group
                ? renderFactRow(r.group, r.label)
                : showEmpty && !onlyUnconfirmed
                  ? (
                    <div key={r.rawKey} className="client-fslot" title={r.rawKey}>
                      <span className="client-fk">{r.label}</span>
                      <span className="n-none">—</span>
                    </div>
                  )
                  : null,
            ).filter(Boolean);
            if (!rendered.length) return null;
            return (
              <div className="client-fgroup" key={group}>
                <div className="client-fgroup-h u-label">
                  {t(group)}
                  <span className="client-fgroup-n src-ref">{prefixes.join(' · ')}</span>
                </div>
                {rendered}
              </div>
            );
          })}

          {briefing.clientSpecific.length > 0 && (
            <div className="client-fgroup">
              <div className="client-fgroup-h u-label" title={t('Facts recorded under novel keys — outside the standing catalog')}>
                {t('Client-specific')}
                <span className="client-fgroup-n voice-figure">{briefing.clientSpecific.length}</span>
              </div>
              {briefing.clientSpecific.map((g) => renderFactRow(g, g.key, true))}
            </div>
          )}
        </div>
      )}

      {/* ── Add fact modal ── */}
      {factDraft && (
        <div className="modal-overlay" onClick={() => setFactDraft(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('Record a fact')}</h2>
              <button className="close-btn" onClick={() => setFactDraft(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">{t('Key — a standing catalog field; reuse an existing key to supersede its value')}</label>
                <input className="form-input mono" placeholder="commercial.license-fee" value={factDraft.key}
                  onChange={(e) => setFactDraft({ ...factDraft, key: e.target.value })} />
                <label className="text-xs text-ink-3 mt-4" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={factDraft.novel}
                    onChange={(e) => setFactDraft({ ...factDraft, novel: e.target.checked })}
                  />
                  {t('Client-specific (novel key) — standing fields have priority; tick only when none fits')}
                </label>
              </div>
              <div className="form-group">
                <label className="form-label">{t('Value — as stated, with units and qualifiers')}</label>
                <input className="form-input" placeholder={t('18.5k CHF/yr excl. VAT, final offer')} value={factDraft.value}
                  onChange={(e) => setFactDraft({ ...factDraft, value: e.target.value })} />
              </div>
              <div className="flex gap-12">
                <div className="form-group" style={{ width: 150 }}>
                  <label className="form-label">{t('As of')}</label>
                  <input className="form-input mono" type="date" value={factDraft.as_of}
                    onChange={(e) => setFactDraft({ ...factDraft, as_of: e.target.value })} />
                </div>
                <div className="form-group flex-1">
                  <label className="form-label">{t('Stated by')}</label>
                  <input className="form-input" value={factDraft.stated_by}
                    onChange={(e) => setFactDraft({ ...factDraft, stated_by: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('Source document — mandatory; where the value is stated')}</label>
                <select className="form-select" value={factDraft.source_document_id}
                  onChange={(e) => setFactDraft({ ...factDraft, source_document_id: e.target.value })}>
                  <option value="">{t('— pick a document —')}</option>
                  {(libraryDocs ?? []).map((d) => (
                    <option key={d.id} value={d.id}>{d.ref} — {d.original_filename}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('Quote — optional, but must be verbatim from the document')}</label>
                <textarea className="form-textarea" rows={2} value={factDraft.quote}
                  onChange={(e) => setFactDraft({ ...factDraft, quote: e.target.value })} />
                <div className="text-xs text-ink-3 mt-4">
                  {t('Checked character-for-character against the document’s extracted text — a paraphrase is rejected.')}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setFactDraft(null)}>{t('Cancel')}</button>
              <button
                className="btn btn-accent"
                disabled={factSaving || !factDraft.key.trim() || !factDraft.value.trim() || !factDraft.source_document_id}
                onClick={() => void saveNewFact()}
              >
                {factSaving ? t('Recording…') : t('Record fact')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirm}
        title={t('Delete fact')}
        confirmLabel={busy ? t('Deleting…') : t('Delete')}
        danger
        onCancel={() => setConfirm(null)}
        onConfirm={() => void runDelete()}
      >
        {confirm && (
          <p className="text-sm text-ink-2">
            {tx(t, 'Delete this value of {key}?', { key: <span className="mono">{confirm.label}</span> })}{' '}
            {confirm.isCurrent && confirm.hasPredecessor
              ? t('The previous value in the chain becomes current again.')
              : t('The rest of the chain re-links around it.')}{' '}
            {t('The audit log keeps the record.')}
          </p>
        )}
      </ConfirmModal>
    </section>
  );
}
